import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateClient, canDeleteClient, deleteClient, sentenceCase, formatClientNameForSave } from '../utils/storage';
import { getPermissions } from '../utils/permissions';
import AccessDeniedModal from './AccessDeniedModal';
import { ModalTemplate } from './templates';
import './Modal.css';

export default function EditClientModal({ client, onClose, onUpdated, onDeleted }) {
  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.roles || []);
  const [showDeleteDenied, setShowDeleteDenied] = useState(false);
  const [testSiteName, setTestSiteName] = useState(client.name || '');
  const [editingName, setEditingName] = useState(false);
  const [clientName, setClientName] = useState(client.clientName || '');
  const [contactPerson, setContactPerson] = useState(client.contactPerson || '');
  const [addressLine, setAddressLine] = useState(client.addressLine || '');
  const [postCode, setPostCode] = useState(client.postCode || '');
  const [city, setCity] = useState(client.city || '');
  const [country, setCountry] = useState(client.country || '');
  const [deletable, setDeletable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Check if client can be deleted (async)
  useEffect(() => {
    canDeleteClient(client.id).then(setDeletable);
  }, [client.id]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!clientName.trim() || !city.trim()) return;
    setSaving(true);
    try {
      await updateClient(client.id, {
        name: testSiteName.trim(),
        clientName: formatClientNameForSave(clientName),
        contactPerson: sentenceCase(contactPerson),
        addressLine: sentenceCase(addressLine),
        postCode: postCode.trim().toUpperCase(),
        city: sentenceCase(city),
        country: sentenceCase(country),
      });
      onUpdated();
    } catch (err) {
      console.error('Failed to update client:', err);
      setSaveError('Failed to save changes. Please check your connection and try again.');
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deletable) return;
    if (!perms.canDeleteTestSite) {
      setShowDeleteDenied(true);
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to delete this test site? This action cannot be undone.'
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      await deleteClient(client.id);
      onDeleted();
    } catch (err) {
      console.error('Failed to delete client:', err);
      setSaveError('Failed to delete test site. Please try again.');
      setSaving(false);
    }
  }

  function handleNameDoubleClick() {
    setEditingName(true);
  }

  function handleNameBlur() {
    setEditingName(false);
    if (!testSiteName.trim()) {
      setTestSiteName(client.name);
    }
  }

  function handleNameKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditingName(false);
      if (!testSiteName.trim()) {
        setTestSiteName(client.name);
      }
    }
    if (e.key === 'Escape') {
      setEditingName(false);
      setTestSiteName(client.name);
    }
  }

  return (
    <>
      <ModalTemplate title="Edit Test Site" onClose={onClose} wide isDirty={isDirty} closeOnBackdrop={false}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="editTestSiteName">Test Site</label>
            {editingName ? (
              <input
                id="editTestSiteName"
                type="text"
                value={testSiteName}
                onChange={(e) => { setTestSiteName(e.target.value); setIsDirty(true); setSaveError(null); }}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                autoFocus
              />
            ) : (
              <div
                className="locked-field"
                onDoubleClick={handleNameDoubleClick}
                title="Double-click to edit"
              >
                <span className="locked-field-value">{testSiteName}</span>
                <span className="locked-field-icon">🔒</span>
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="editClientName">Client Name</label>
              <input
                id="editClientName"
                type="text"
                value={clientName}
                onChange={(e) => { setClientName(e.target.value); setIsDirty(true); setSaveError(null); }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="editContactPerson">Contact Person</label>
              <input
                id="editContactPerson"
                type="text"
                value={contactPerson}
                onChange={(e) => { setContactPerson(e.target.value); setIsDirty(true); setSaveError(null); }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="editAddressLine">Address Line</label>
            <input
              id="editAddressLine"
              type="text"
              value={addressLine}
              onChange={(e) => { setAddressLine(e.target.value); setIsDirty(true); setSaveError(null); }}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="editPostCode">Post Code</label>
              <input
                id="editPostCode"
                type="text"
                value={postCode}
                onChange={(e) => { setPostCode(e.target.value); setIsDirty(true); setSaveError(null); }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="editCity">City</label>
              <input
                id="editCity"
                type="text"
                value={city}
                onChange={(e) => { setCity(e.target.value); setIsDirty(true); setSaveError(null); }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="editCountry">Country</label>
              <input
                id="editCountry"
                type="text"
                value={country}
                onChange={(e) => { setCountry(e.target.value); setIsDirty(true); setSaveError(null); }}
              />
            </div>
          </div>

          {saveError && <div className="alert alert-danger" style={{ marginTop: 12 }}>{saveError}</div>}
          <div className="modal-actions modal-actions-spread">
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleDelete}
              disabled={!deletable || saving}
              title={deletable ? 'Delete this test site' : 'Remove all objects before deleting'}
            >
              Delete Test Site
            </button>
            <div className="modal-actions-right">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </ModalTemplate>
      {showDeleteDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowDeleteDenied(false)}
        />
      )}
    </>
  );
}
